<script>
import Lottie from "@/components/widgets/lottie.vue";
import animationData from "@/components/widgets/tqywkdcz.json";

export default {
  name: "OtpVerificationModal",
  components: { Lottie },

  data() {
    return {
      digits: ["", "", "", "", "", ""],
      loading: false,
      resendingOtp: false,
      step: "verify", // verify | success
      trustDevice: false,
      apiResource: '/service/centralize-authenticate/api/tenant/v1/security',

      verificationId: null,

      resendCooldown: 60,
      resendTimer: null,

      lottieOptions: { animationData },
    };
  },

  computed: {
    verificationCode() {
      return this.digits.join("");
    }
  },

  methods: {
    openModal(data = null) {
      this.resetState();
      this.verificationId = data?.verification_id || null;
      if (this.verificationId) {
        this.startResendTimer();
      }
      this.$refs.otpVerificationModal.show();
    },

    closeModal() {
      this.resetState();
      this.$emit("closed");
      // Notify axios interceptor if cancelled
      window.dispatchEvent(
          new CustomEvent("otp-verification-completed", {
            detail: { success: false, cancelled: true },
          })
      );
      this.$refs.otpVerificationModal.hide();
    },

    resetState() {
      this.step = "verify";
      this.digits = ["", "", "", "", "", ""];
      this.loading = false;
      this.verificationId = null;
      this.trustDevice = false;

      clearInterval(this.resendTimer);
      this.resendCooldown = 60;
    },

    focusInput(index) {
      this.$nextTick(() => {
        const el = this.$refs[`digit${index}`]?.[0];
        if (el) el.focus();
      });
    },

    onKeyDown(index, event) {
      const key = event.key;

      if (key === "Backspace") {
        if (this.digits[index]) {
          this.digits[index] = "";
        } else if (index > 0) {
          this.digits[index - 1] = "";
          this.focusInput(index - 1);
        }
        event.preventDefault();
      }

      else if (/^\d$/.test(key)) {
        this.digits[index] = key;

        if (index < 5) {
          this.focusInput(index + 1);
        }

        event.preventDefault();
      }

      else if (key === "Enter") {
        // Press Enter to verify (anytime)
        if (this.verificationCode.length === 6) {
          this.verifyCode();
        }
      }
    },

    async verifyCode() {
      if (this.loading) return;

      this.loading = true;
      try {
        const response = await this.$axios.post(
            `${this.apiResource}/verify`,
            {
              code: this.verificationCode,
              security_verification_id: this.verificationId,
              trust_device: this.trustDevice
            }
        );

        if (response.data.status) {
          this.step = "success";
          this.toastSuccess("Success", response.data.message);
          // Notify axios interceptor if verified
          window.dispatchEvent(
              new CustomEvent("otp-verification-completed", {
                detail: {
                  success: true,
                  security_verification_id: response.data.data?.security_verification_id || null
                },
              })
          );
          // Slight delay for user feedback before closing
          setTimeout(() => {
            this.$emit("completed");
            this.$refs.otpVerificationModal.hide();
          }, 1200);
        } else {
          this.toastError("Error", response.data.message);
        }
      } catch (error) {
        this.errorHandler(error);
      } finally {
        this.loading = false;
      }
    },

    async resendOtp() {
      if (this.resendCooldown > 0 || this.resendingOtp) return;

      this.resendingOtp = true;
      try {
        const response = await this.$axios.post(
            `${this.apiResource}/verifications/${this.verificationId}/trigger`
        );

        if (response.data.status) {
          this.toastSuccess("Success", "OTP resent!");
          this.startResendTimer();
        } else {
          this.toastError("Error", response.data.message);
        }
      } catch (e) {
        this.errorHandler(e);
      } finally {
        this.resendingOtp = false;
      }
    },

    startResendTimer() {
      this.resendCooldown = 60;

      clearInterval(this.resendTimer);

      this.resendTimer = setInterval(() => {
        if (this.resendCooldown <= 1) {
          this.resendCooldown = 0;
          clearInterval(this.resendTimer);
        } else {
          this.resendCooldown--;
        }
      }, 1000);
    }
  }
};
</script>

<template>
  <BModal
      ref="otpVerificationModal"
      centered
      modal-class="flip"
      class="v-modal-custom"
      hide-footer
      header-class="border-bottom pb-3"
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      @shown="focusInput(0)"
      style="z-index: 2000"
  >
    <template #header>
      <div class="d-flex align-items-center gap-2">
        <i class="mdi mdi-form-textbox-password fs-4 text-primary"></i>
        <h5 class="mb-0 fw-semibold text-capitalize">{{ $t("t-method-verification", { method: "OTP"}) }}</h5>
      </div>
    </template>

    <!-- Step: Verify OTP -->
    <div v-if="step === 'verify'" class="card mt-3 border-0 shadow-sm rounded-3">
      <div class="card-body p-4">
        <div class="mb-3 text-center">
          <div class="avatar-lg mx-auto">
            <div
                class="avatar-title bg-light text-primary display-5 rounded-circle"
            >
              <i class="ri-shield-check-line"></i>
            </div>
          </div>
        </div>

        <div class="p-2 mt-3">
          <div class="text-center text-muted mb-3 mx-lg-3">
            <h4 class="text-capitalize mb-1">{{ $t("t-verify-otp") }}</h4>
            <p class="mb-0">{{ $t("t-enter-verify-code", { digit: 6 }) }}</p>

            <small class="text-warning fw-semibold">
              <i class="ri-time-line me-2 fs-5 text-warning"></i>
              <span>{{ $t('t-please-verify-within-mins', { minutes: 5 }) }}</span>
            </small>
          </div>

          <!-- OTP boxes -->
          <form autocomplete="off">
            <div class="row justify-content-center">
              <div
                  v-for="(digit, index) in digits"
                  :key="index"
                  class="col-2 px-1"
              >
                <input
                    type="text"
                    maxlength="1"
                    :ref="'digit' + index"
                    :id="'digit'+(index+1)"
                    v-model="digits[index]"
                    @keydown="onKeyDown(index, $event)"
                    class="form-control form-control-lg bg-light border-light text-center fw-semibold"
                    :disabled="loading"
                />
              </div>
            </div>
          </form>

          <!-- Resend OTP -->
          <div class="text-center mt-4">
            <button
                class="btn btn-outline-primary btn-sm resend-btn"
                :disabled="resendCooldown > 0 || resendingOtp"
                @click="resendOtp"
            >
              <i class="ri-refresh-line me-1"></i>
              <span v-if="resendCooldown === 0">{{ $t("t-resend-otp") }}</span>
              <span v-else-if="resendingOtp">{{ $t("t-resending-otp") + "..." }}</span>
              <span v-else>{{ $t("t-resend-otp") }} in {{ resendCooldown }}s</span>
            </button>
          </div>

          <!-- Trust this device -->
          <div class="mt-4 text-start d-flex align-items-start gap-2">
            <input
                class="form-check-input mt-1"
                type="checkbox"
                id="trustDeviceOtp"
                v-model="trustDevice"
                :disabled="loading"
            />
            <label class="form-check-label small text-muted" for="trustDeviceOtp">
              <span class="fw-semibold text-body">{{ $t('t-trust-device') }}</span><br />
              <small>{{ $t('t-trust-device-desc') }}</small>
            </label>
          </div>

          <div class="mt-4 d-flex gap-2">
            <BButton
                variant="outline-secondary"
                class="w-50"
                @click="closeModal"
                :disabled="loading"
            >
              Cancel
            </BButton>

            <BButton
                variant="success"
                class="w-50"
                @click="verifyCode"
                :disabled="loading || verificationCode.length < 6"
            >
              <i class="mdi mdi-lock-check-outline fs-5"></i>
              {{ loading ? $t("t-verifying") + "..." : $t("t-verify") }}
            </BButton>
          </div>
        </div>
      </div>
    </div>

    <!-- Step: Success -->
    <div v-else-if="step === 'success'" class="text-center py-4">
      <div class="mb-4">
        <Lottie
            :options="lottieOptions"
            trigger="loop"
            :height="120"
            :width="120"
        />
      </div>
      <h5 class="text-success fw-semibold text-capitalize">
        {{ $t("t-verified-successfully") }}
      </h5>
      <p class="text-muted small mb-0 fs-6"> {{ $t("t-loading") }}<span class="loading-dots"></span></p>
    </div>
  </BModal>
</template>

<style scoped>
.v-modal-custom {
  z-index: 99999 !important;
}

.modal-backdrop {
  z-index: 99998 !important;
}

.avatar-lg {
  width: 80px;
  height: 80px;
}

.avatar-title {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

input.form-control-lg {
  font-size: 1.5rem;
}

input.form-control-lg:focus {
  border-color: var(--bs-success);
  box-shadow: 0 0 0 0.2rem rgba(25,135,84,0.25);
}

.loading-dots::after {
  content: '';
  display: inline-block;
  animation: dots 1s steps(5, end) infinite;
}

@keyframes dots {
  0% { content: ''; }
  20% { content: '.'; }
  40% { content: '..'; }
  60% { content: '...'; }
  80% { content: '....'; }
  100% { content: ''; }
}

.resend-btn {
  border-radius: 50px;
  padding: 6px 18px;
  font-weight: 500;
  transition: 0.2s;
}

.resend-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
